<template>
  <div class="budget-view">
    <div class="budget-header">
      <router-link to="/" class="back-link">← Back</router-link>
      <h2>Budget Categories</h2>
    </div>

    <p v-if="loading" class="status-msg">Loading categories...</p>
    <p v-if="error" class="error">{{ error }}</p>

    <div v-if="!loading && !error" class="budget-content">
      <div class="summary-card">
        <div class="summary-totals">
          <div class="summary-total">
            <span class="summary-label">Total budget</span>
            <span class="summary-value">${{ fmt(totalBudget) }}<span class="summary-freq">/mo</span></span>
          </div>
          <div class="summary-breakdown">
            <div class="breakdown-item breakdown-fixed">
              <span class="breakdown-dot"></span>
              <span class="breakdown-label">Fixed</span>
              <span class="breakdown-value">${{ fmt(fixedBudget) }}</span>
              <span class="breakdown-pct">{{ fixedPct }}%</span>
            </div>
            <div class="breakdown-item breakdown-disc">
              <span class="breakdown-dot"></span>
              <span class="breakdown-label">Discretionary</span>
              <span class="breakdown-value">${{ fmt(discretionaryBudget) }}</span>
              <span class="breakdown-pct">{{ discPct }}%</span>
            </div>
          </div>
        </div>
        <div class="summary-bar-bg">
          <div class="summary-bar-fixed" :style="{ width: fixedPct + '%' }"></div>
          <div class="summary-bar-disc" :style="{ width: discPct + '%' }"></div>
        </div>
      </div>

      <table class="cat-table">
        <thead>
          <tr>
            <th class="th-icon"></th>
            <th>Slug</th>
            <th>Display Name</th>
            <th>Monthly Budget</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cat, idx) in localCategories" :key="cat._uid" :class="{ 'row-fixed': isFixed(cat.name) }">
            <td class="icon-cell">
              <i :class="['fas', iconFor(cat.name)]"></i>
            </td>
            <td class="slug-cell">
              <input
                v-if="cat._isNew"
                v-model="cat.name"
                class="input-slug"
                placeholder="slug_name"
              />
              <span v-else class="slug-text">{{ cat.name }}</span>
            </td>
            <td>
              <input v-model="cat.display_name" class="input-text" placeholder="Display Name" />
            </td>
            <td>
              <div class="budget-input-wrap">
                <span class="dollar-sign">$</span>
                <input
                  v-model.number="cat.budgeted"
                  type="number"
                  min="0"
                  step="50"
                  class="input-number"
                />
              </div>
            </td>
            <td class="action-cell">
              <button class="btn-delete" @click="removeCategory(idx)" title="Remove">×</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="footer-actions">
        <button class="btn-add" @click="addCategory">+ Add Category</button>
        <button class="btn-save" disabled title="Saving not yet implemented">Save Changes</button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { get } from '@/common/api';

const ICONS = {
  gym_memberships: 'fa-dumbbell',
  rent: 'fa-home',
  daily_travel: 'fa-subway',
  health_and_fitness: 'fa-heartbeat',
  car_payment: 'fa-car',
  utilities: 'fa-lightbulb',
  steep_hollow: 'fa-tree',
  subscriptions: 'fa-podcast',
  zogchow: 'fa-utensils',
  car_insurance: 'fa-shield-alt',
  clothing: 'fa-tshirt',
  discretionary: 'fa-store',
  car_maintenance: 'fa-wrench',
  other: 'fa-folder',
  entertainment: 'fa-mountain',
  groceries: 'fa-carrot',
};

const BILL_CATEGORIES = new Set([
  'rent', 'car_payment', 'car_insurance', 'utilities',
  'subscriptions', 'gym_memberships', 'steep_hollow',
]);

let _uid = 0;

export default defineComponent({
  name: 'BudgetView',
  data() {
    return {
      localCategories: [],
      loading: true,
      error: null,
    };
  },
  computed: {
    totalBudget() {
      return this.localCategories.reduce((s, c) => s + (c.budgeted || 0), 0);
    },
    fixedBudget() {
      return this.localCategories
        .filter(c => BILL_CATEGORIES.has(c.name))
        .reduce((s, c) => s + (c.budgeted || 0), 0);
    },
    discretionaryBudget() {
      return this.localCategories
        .filter(c => !BILL_CATEGORIES.has(c.name))
        .reduce((s, c) => s + (c.budgeted || 0), 0);
    },
    fixedPct() {
      if (!this.totalBudget) return 0;
      return Math.round((this.fixedBudget / this.totalBudget) * 100);
    },
    discPct() {
      if (!this.totalBudget) return 0;
      return 100 - this.fixedPct;
    },
  },
  methods: {
    iconFor(name) {
      return ICONS[name] || 'fa-tag';
    },
    isFixed(name) {
      return BILL_CATEGORIES.has(name);
    },
    fmt(val) {
      return Math.round(val || 0).toLocaleString();
    },
    addCategory() {
      this.localCategories.push({
        _uid: ++_uid,
        _isNew: true,
        name: '',
        display_name: '',
        budgeted: 0,
        spent: 0,
      });
    },
    removeCategory(idx) {
      this.localCategories.splice(idx, 1);
    },
  },
  mounted() {
    get('categories')
      .then(r => r.json())
      .then(data => {
        this.localCategories = (Array.isArray(data) ? data : data.categories || []).map(c => ({
          ...c,
          _uid: ++_uid,
          _isNew: false,
        }));
        this.loading = false;
      })
      .catch(err => {
        this.error = 'Failed to load categories: ' + err.message;
        this.loading = false;
      });
  },
});
</script>

<style scoped>
.budget-view {
  max-width: 700px;
  margin: 2rem auto;
  padding: 0 1rem 3rem;
}

.budget-header {
  display: flex;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.back-link {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
}
.back-link:hover { color: #374151; }

.budget-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.status-msg {
  color: #9ca3af;
  text-align: center;
  padding: 2rem;
}

.error { color: #dc2626; }

/* Summary card */
.summary-card {
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px 14px;
  margin-bottom: 1.5rem;
}

.summary-totals {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.summary-total {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.summary-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  font-weight: 500;
}
.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}
.summary-freq {
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 400;
  margin-left: 2px;
}

.summary-breakdown {
  display: flex;
  gap: 1.5rem;
}
.breakdown-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
}
.breakdown-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.breakdown-fixed .breakdown-dot { background: #6366f1; }
.breakdown-disc  .breakdown-dot { background: #10b981; }

.breakdown-label { color: #6b7280; }
.breakdown-value { font-weight: 600; color: #374151; }
.breakdown-pct {
  color: #9ca3af;
  font-size: 0.75rem;
}

.summary-bar-bg {
  height: 8px;
  border-radius: 4px;
  background: #f3f4f6;
  overflow: hidden;
  display: flex;
}
.summary-bar-fixed {
  height: 100%;
  background: #6366f1;
  transition: width 0.4s ease;
}
.summary-bar-disc {
  height: 100%;
  background: #10b981;
  transition: width 0.4s ease;
}

/* Table */
.cat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.cat-table thead th {
  text-align: left;
  padding: 6px 10px;
  color: #9ca3af;
  font-weight: 500;
  border-bottom: 1.5px solid #e5e7eb;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.th-icon { width: 36px; }

.cat-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;
}
.cat-table tbody tr:hover { background: #f9fafb; }

.row-fixed .icon-cell { color: #6366f1; }

.cat-table td {
  padding: 8px 10px;
  vertical-align: middle;
}

.icon-cell {
  color: #10b981;
  font-size: 14px;
  text-align: center;
  width: 36px;
}

.slug-cell { width: 160px; }

.slug-text {
  font-family: monospace;
  font-size: 0.8rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}

.input-text,
.input-slug,
.input-number {
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 0.875rem;
  color: #111827;
  background: #fff;
  transition: border-color 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.input-slug { font-family: monospace; }
.input-text:focus,
.input-slug:focus,
.input-number:focus {
  outline: none;
  border-color: #4f8ef7;
}

.budget-input-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 120px;
}
.dollar-sign {
  color: #9ca3af;
  font-size: 0.875rem;
  flex-shrink: 0;
}
.input-number { max-width: 90px; }

.action-cell { width: 40px; text-align: center; }

.btn-delete {
  background: none;
  border: none;
  color: #d1d5db;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.btn-delete:hover {
  color: #ef4444;
  background: #fee2e2;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.btn-add {
  background: none;
  border: 1.5px dashed #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  padding: 6px 14px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.btn-add:hover {
  border-color: #4f8ef7;
  color: #4f8ef7;
}

.btn-save {
  background: #4f8ef7;
  border: none;
  border-radius: 6px;
  color: #fff;
  padding: 7px 20px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-save:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}
</style>
